import { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Cctv, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";

export default function LoginPage() {
  const { user, setupRequired, login, error, setError } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (setupRequired === true) return <Navigate to="/setup" replace />;
  if (user) return <Navigate to={location.state?.from || "/cameras"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    await login(email, password);
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#0b0b0d] flex items-center justify-center px-4 py-10" data-testid="login-page">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 size-11 rounded-2xl bg-brand/15 text-brand grid place-items-center">
            <Cctv className="size-5" strokeWidth={1.8} />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-white">CAM</h1>
          <p className="mt-2 text-sm text-zinc-400">Connectez-vous pour accéder à vos caméras.</p>
        </div>

        <form
          onSubmit={submit}
          className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-5 sm:p-6 space-y-4 shadow-2xl shadow-black/20"
          data-testid="login-form"
        >
          <div className="space-y-2">
            <Label htmlFor="login-email" className="text-sm font-medium text-zinc-300">
              Adresse e-mail
            </Label>
            <Input
              id="login-email"
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              autoFocus
              className="h-11 rounded-xl border-white/[0.09] bg-black/20 px-3 text-sm text-white placeholder:text-zinc-600 focus-visible:ring-brand"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="login-password" className="text-sm font-medium text-zinc-300">
              Mot de passe
            </Label>
            <Input
              id="login-password"
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="h-11 rounded-xl border-white/[0.09] bg-black/20 px-3 text-sm text-white placeholder:text-zinc-600 focus-visible:ring-brand"
            />
          </div>

          {error && (
            <div
              className="rounded-xl border border-status-offline/30 bg-status-offline/10 px-3 py-2.5 text-sm text-red-300 flex items-start gap-2"
              data-testid="login-error"
            >
              <ShieldAlert className="size-4 shrink-0 mt-0.5" strokeWidth={1.8} />
              <span>{error}</span>
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="w-full h-11 rounded-xl bg-brand hover:bg-brand-hover text-white font-medium"
          >
            {busy ? "Connexion…" : "Se connecter"}
          </Button>
        </form>
      </div>
    </div>
  );
}
